/**
 * What a meeting tile shows while the camera is off (SC-270): the profile photo, or initials on a
 * colour derived from the user's id when there is no photo or it fails to load.
 *
 * A remote participant's photo comes from their LiveKit participant metadata, which the server
 * writes into the access token at join (`{ userId, photoUrl }`, see server/services/livekit.ts).
 * So others see a new photo after the next join. Our own tile reads the signed-in user instead,
 * and changes as soon as a new photo is saved.
 */

import React, { useEffect, useMemo, useState } from 'react'
import { useAppSelector } from '../../hooks'
import { colorForId } from '../ChatPanel/lib/avatar'
import { getDisplayName } from '../../utils/personName'

export interface CameraOffPictureInfo {
  /** Only ever an http(s) URL. */
  photoUrl?: string
  initials: string
  /** Identity colour behind the initials; fixed per user so a person keeps their colour. */
  colour: string
  /** Accessible name of the picture. */
  label: string
}

export interface ParticipantPictureMetadata {
  userId?: string
  photoUrl?: string
}

/**
 * An http(s) URL, as the dashboard shows the same photo; any other scheme (javascript:, data:) is
 * dropped. On an https page the browser upgrades or blocks an http image itself.
 */
const imageUrl = (value: unknown): string | undefined =>
  typeof value === 'string' && /^https?:\/\//i.test(value) ? value : undefined

/** Reads `{ userId, photoUrl }` from a LiveKit participant's metadata; anything else is ignored. */
export function parseParticipantMetadata(metadata: string | undefined | null): ParticipantPictureMetadata {
  if (!metadata) return {}
  try {
    const parsed = JSON.parse(metadata)
    if (!parsed || typeof parsed !== 'object') return {}
    return {
      userId: typeof parsed.userId === 'string' && parsed.userId ? parsed.userId : undefined,
      photoUrl: imageUrl(parsed.photoUrl),
    }
  } catch {
    return {}
  }
}

/** First letters of the first and last word, e.g. "Namrata Chheda" → "NC". */
export function initialsFor(name: string | undefined | null): string {
  const words = (name || '').trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) return '?'
  const first = Array.from(words[0])[0]
  const last = words.length > 1 ? Array.from(words[words.length - 1])[0] : ''
  return `${first}${last}`.toUpperCase()
}

export function cameraOffPicture(input: { name: string; colourKey: string; photoUrl?: string }): CameraOffPictureInfo {
  return {
    photoUrl: imageUrl(input.photoUrl),
    initials: initialsFor(input.name),
    colour: colorForId(input.colourKey || input.name || '?'),
    label: input.name || 'Participant',
  }
}

/** For a remote participant: name and identity from LiveKit, photo and user id from its metadata. */
export function remoteCameraOffPicture(participant: {
  identity: string
  name?: string
  metadata?: string
}): CameraOffPictureInfo {
  const { userId, photoUrl } = parseParticipantMetadata(participant.metadata)
  return cameraOffPicture({
    name: participant.name || participant.identity.slice(0, 8),
    colourKey: userId || participant.identity,
    photoUrl,
  })
}

/** For our own tile: the signed-in user, so a saved photo shows without rejoining. */
export function useLocalCameraOffPicture(): CameraOffPictureInfo {
  const userData = useAppSelector((state) => state.user.userData)
  const name = getDisplayName(userData, 'You')
  const colourKey = userData?.id || name
  const photoUrl = userData?.customAvatarUrl
  // Stable while nothing it shows changes: the PiP model depends on it and is rebuilt when it changes.
  return useMemo(() => cameraOffPicture({ name, colourKey, photoUrl }), [name, colourKey, photoUrl])
}

/** The camera-off picture for a tile, local or remote. */
export function useCameraOffPicture(
  participant: { identity: string; name?: string; metadata?: string } | undefined,
  isLocal: boolean,
  fallbackName: string
): CameraOffPictureInfo {
  const local = useLocalCameraOffPicture()
  if (isLocal) return local
  return participant
    ? remoteCameraOffPicture(participant)
    : cameraOffPicture({ name: fallbackName, colourKey: fallbackName })
}

/**
 * The camera track to draw, or undefined while the camera is off.
 *
 * LiveKit keeps a track reference for a camera that has been switched off: the publication survives,
 * muted. Drawing it leaves a black or frozen tile where the camera-off picture belongs (the same reason
 * picture-in-picture decides with `pipCameraState`). Our own camera also follows the toggle, because the
 * track outlives it.
 */
export function liveCameraTrack(
  cameraTrack: any,
  isLocal: boolean,
  localVideoEnabled: boolean,
  participant: { isCameraEnabled?: boolean } | undefined
): any {
  if (!cameraTrack) return undefined
  if (isLocal && !localVideoEnabled) return undefined
  if (participant?.isCameraEnabled === false) return undefined
  if (cameraTrack.publication?.isMuted) return undefined
  return cameraTrack
}

export interface CameraOffPictureProps {
  picture: CameraOffPictureInfo
  /** Diameter: a CSS length. Defaults to a share of the tile so it scales with it. */
  size?: string
  style?: React.CSSProperties
}

/** The round photo, or the initials when there is no photo or it does not load. */
export function CameraOffPicture({ picture, size = '55%', style }: CameraOffPictureProps) {
  const [failedUrl, setFailedUrl] = useState<string | undefined>(undefined)
  useEffect(() => setFailedUrl(undefined), [picture.photoUrl])
  const showPhoto = Boolean(picture.photoUrl) && failedUrl !== picture.photoUrl

  const frame: React.CSSProperties = {
    height: size,
    maxHeight: '160px',
    maxWidth: '80%',
    aspectRatio: '1 / 1',
    borderRadius: '50%',
    overflow: 'hidden',
    flexShrink: 0,
    ...style,
  }

  if (showPhoto) {
    return (
      <img
        src={picture.photoUrl}
        alt={picture.label}
        data-testid="camera-off-photo"
        style={{ ...frame, objectFit: 'cover', display: 'block' }}
        onError={() => setFailedUrl(picture.photoUrl)}
      />
    )
  }

  // SVG, so the letters scale with the circle whatever size the tile is.
  return (
    <svg
      viewBox="0 0 100 100"
      role="img"
      aria-label={picture.label}
      data-testid="camera-off-initials"
      style={{ ...frame, display: 'block' }}
    >
      <circle cx="50" cy="50" r="50" fill={picture.colour} />
      <text
        x="50"
        y="50"
        dominantBaseline="central"
        textAnchor="middle"
        fontSize={picture.initials.length > 1 ? 38 : 46}
        fontWeight={700}
        fill="var(--sevv-white, #ffffff)"
      >
        {picture.initials}
      </text>
    </svg>
  )
}
